import { useEffect, useRef, useState } from "react";
import { View, useWindowDimensions } from "react-native";
import {
  FlaskConical,
  HelpCircle,
  Pill,
  RefreshCw,
  Snowflake,
  Syringe,
} from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useTranslations } from "@src/packages/i18n";
import type {
  OnboardingStackParamList,
  PlannedTreatmentModality,
} from "../types";
import { useOnboarding } from "../OnboardingContext";
import OnboardingLayout, {
  ScreenSubtitle,
  ScreenTitle,
} from "../components/OnboardingLayout";
import GlassChoiceButton from "../components/GlassChoiceButton";
import { getOnboardingViewport } from "../onboardingViewport";
import { AUTO_ADVANCE_DELAY } from "../theme";

type Props = NativeStackScreenProps<OnboardingStackParamList, "TreatmentType">;

type TreatmentOption = {
  value: PlannedTreatmentModality;
  labelKey: string;
  sublabelKey: string;
  Icon: typeof FlaskConical;
};

const OPTIONS: TreatmentOption[] = [
  {
    value: "ivf",
    labelKey: "onboarding.treatment_type.ivf_label",
    sublabelKey: "onboarding.treatment_type.ivf_sublabel",
    Icon: FlaskConical,
  },
  {
    value: "iui",
    labelKey: "onboarding.treatment_type.iui_label",
    sublabelKey: "onboarding.treatment_type.iui_sublabel",
    Icon: Syringe,
  },
  {
    value: "oi",
    labelKey: "onboarding.treatment_type.oi_label",
    sublabelKey: "onboarding.treatment_type.oi_sublabel",
    Icon: Pill,
  },
  {
    value: "embryo_freezing",
    labelKey: "onboarding.treatment_type.embryo_freezing_label",
    sublabelKey: "onboarding.treatment_type.embryo_freezing_sublabel",
    Icon: Snowflake,
  },
  {
    value: "fet",
    labelKey: "onboarding.treatment_type.fet_label",
    sublabelKey: "onboarding.treatment_type.fet_sublabel",
    Icon: RefreshCw,
  },
];

const NOT_SURE_OPTION: TreatmentOption = {
  value: "not-sure",
  labelKey: "onboarding.treatment_type.not_sure_label",
  sublabelKey: "onboarding.treatment_type.not_sure_sublabel",
  Icon: HelpCircle,
};

export default function TreatmentTypeScreen({ navigation, route }: Props) {
  const { width, height } = useWindowDimensions();
  const { condensed } = getOnboardingViewport(width, height);
  const compactChoices = condensed || width < 430;
  const t = useTranslations();
  const { data, updateField } = useOnboarding();
  const { stage } = route.params;
  const options =
    stage === "getting-ready" ? [...OPTIONS, NOT_SURE_OPTION] : OPTIONS;
  const existingValue =
    stage === "getting-ready"
      ? data.plannedTreatmentModality
      : data.currentTreatmentFamily;
  const [selected, setSelected] = useState<PlannedTreatmentModality | null>(
    existingValue,
  );
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const advancing = useRef(false);

  useEffect(
    () => () => {
      if (advanceTimer.current) clearTimeout(advanceTimer.current);
    },
    [],
  );

  const handleSelect = (option: TreatmentOption) => {
    if (advancing.current) return;
    advancing.current = true;
    setSelected(option.value);

    if (stage === "getting-ready") {
      updateField("plannedTreatmentModality", option.value);
      updateField("currentTreatmentFamily", null);
    } else if (option.value !== "not-sure") {
      updateField("currentTreatmentFamily", option.value);
      updateField("plannedTreatmentModality", null);
    }

    advanceTimer.current = setTimeout(() => {
      advancing.current = false;
      advanceTimer.current = null;
      navigation.navigate("Birthday");
    }, AUTO_ADVANCE_DELAY);
  };

  return (
    <OnboardingLayout>
      <ScreenTitle>
        {t(
          stage === "getting-ready"
            ? "onboarding.treatment_type.getting_ready_title"
            : "onboarding.treatment_type.in_treatment_title",
        )}
      </ScreenTitle>
      <ScreenSubtitle>
        {t(
          stage === "getting-ready"
            ? "onboarding.treatment_type.getting_ready_subtitle"
            : "onboarding.treatment_type.in_treatment_subtitle",
        )}
      </ScreenSubtitle>

      <View style={{ gap: compactChoices ? 10 : 14 }}>
        {options.map((option) => (
          <GlassChoiceButton
            key={option.value}
            Icon={option.Icon}
            label={t(option.labelKey)}
            sublabel={t(option.sublabelKey)}
            onPress={() => handleSelect(option)}
            compact={compactChoices}
            selected={selected === option.value}
          />
        ))}
      </View>
    </OnboardingLayout>
  );
}
